import React, { useState } from 'react';
import {
  Star,
  ExternalLink,
  Sparkles,
  Scale,
  Edit3,
  Trash2,
  RefreshCw,
  ImageIcon,
  TrendingDown,
} from '../../icons';
import Menu from '../ui/Menu';
import Sheet from '../ui/Sheet';
import Button from '../ui/Button';
import PlatformBadge from '../ui/PlatformBadge';
import { cn } from '../ui/cn';

const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : null;
};

export const formatEuro = (v) => {
  const n = num(v);
  return n === null ? 'N/A' : `€${n.toLocaleString()}`;
};

/** Returns { startPrice, dropPct } when the listing price fell below its start price. */
export const getPriceDrop = (property) => {
  const start = num(property.startPrice);
  const current = num(property.lastKnownPrice);
  if (!start || !current || current <= 0 || start <= current) return null;
  return { startPrice: start, dropPct: Math.round(((start - current) / start) * 100) };
};

/** `beds · baths · m² · year` — omits missing values. */
export const getStatRow = (property) => {
  const parts = [];
  if (property.bedrooms) parts.push(`${property.bedrooms} bd`);
  if (property.bathrooms) parts.push(`${property.bathrooms} ba`);
  if (property.area) parts.push(`${property.area} m²`);
  if (property.constructionYear && property.constructionYear !== 'N/A') parts.push(String(property.constructionYear));
  return parts;
};

/** Delete confirmation dialog shared by the card and the detail view. */
export const DeleteConfirmDialog = ({ isOpen, onClose, onConfirm, property }) => (
  <Sheet isOpen={isOpen} onClose={onClose} mode="dialog" size="sm" title="Delete property" aria-label="Delete property">
    <p className="text-sm text-primary">
      Are you sure you want to delete{' '}
      <strong>{property?.title ? `"${property.title}"` : 'this property'}</strong>?
    </p>
    <p className="text-xs text-secondary mt-2">This will remove the property and all its history. This cannot be undone.</p>
    <div className="flex justify-end gap-2 mt-5">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="danger-solid"
        onClick={() => {
          onClose();
          onConfirm();
        }}
      >
        <Trash2 className="w-4 h-4" /> Delete
      </Button>
    </div>
  </Sheet>
);

/**
 * Photo-first property card. Tapping the body calls `onOpen(property)`;
 * actions live in the overflow menu. Handler signatures match the previous card.
 */
const PropertyCard = ({
  property,
  onOpen,
  onEdit,
  onDelete,
  onToggleFavorite,
  onAnalyze,
  onCompare,
  onRefetch,
  isRefetching,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  const drop = getPriceDrop(property);
  const stats = getStatRow(property);
  const sold = !!property.sold;
  const showImage = property.imageURL && !imgFailed;

  const menuItems = [
    property.url && { label: 'Open listing', icon: ExternalLink, href: property.url },
    { label: 'Analyze', icon: Sparkles, onSelect: () => onAnalyze(property), disabled: sold },
    { label: 'Compare', icon: Scale, onSelect: () => onCompare(property), disabled: sold },
    { label: isRefetching ? 'Fetching…' : 'Refetch', icon: RefreshCw, onSelect: () => onRefetch(property.id), disabled: isRefetching },
    { label: 'Edit', icon: Edit3, onSelect: () => onEdit(property), disabled: sold },
    { label: 'Delete', icon: Trash2, onSelect: () => setShowDeleteConfirm(true), danger: true },
  ];

  const open = () => onOpen && onOpen(property);

  return (
    <article
      data-testid="property-card"
      data-sold={sold ? 'true' : 'false'}
      className={cn(
        'group relative flex flex-col bg-card border rounded-card shadow-card overflow-hidden transition-[border-color,transform] duration-150',
        property.isFavorite ? 'border-amber/50' : 'border-line hover:border-line-strong',
        sold && 'opacity-80'
      )}
    >
      {/* Media */}
      <button
        type="button"
        onClick={open}
        aria-label={`Open ${property.title || 'property'}`}
        className="relative block w-full aspect-[16/10] bg-card-2 overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
      >
        {showImage ? (
          <img
            src={property.imageURL}
            alt={property.title || 'Property'}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#213043] to-[#152131] flex items-center justify-center text-muted">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}
        {/* Overlays */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          <PlatformBadge platform={property.platform} />
          {property.isDistressed && (
            <span className="px-1.5 py-0.5 rounded-md bg-amber text-[#2A1C00] text-[11px] font-semibold">Distressed</span>
          )}
        </div>
        {drop && !sold && (
          <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-danger text-white text-xs font-semibold shadow">
            <TrendingDown className="w-3.5 h-3.5" /> -{drop.dropPct}%
          </span>
        )}
        {sold && (
          <span className="absolute top-3 -right-8 rotate-45 w-32 text-center bg-danger text-white text-[11px] font-bold tracking-wider py-1 shadow">
            SOLD
          </span>
        )}
      </button>

      {/* Favorite star (outside the media button so it never opens the detail) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(property.id);
        }}
        aria-label={property.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={!!property.isFavorite}
        className={cn(
          'absolute top-1.5 right-1.5 w-10 h-10 flex items-center justify-center rounded-full transition-colors',
          sold ? 'top-1.5 right-14' : '',
          property.isFavorite ? 'text-amber' : 'text-white/80 hover:text-amber'
        )}
      >
        <Star className="w-5 h-5 drop-shadow" filled={property.isFavorite} />
      </button>

      {/* Body */}
      <div className="flex flex-col flex-1 p-3.5 pt-3">
        <button type="button" onClick={open} className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded-md">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={cn('text-[20px] font-bold tabular tracking-tight', sold ? 'text-secondary' : 'text-accent')}>
              {sold ? 'Sold' : formatEuro(property.lastKnownPrice)}
            </span>
            {drop && !sold && (
              <span className="text-xs text-muted line-through tabular">was {formatEuro(drop.startPrice)}</span>
            )}
            {property.forRent && !property.forSale && !sold && <span className="text-xs text-secondary">/ month</span>}
          </div>
          <h3 className="mt-1 text-[15px] font-semibold text-primary leading-snug line-clamp-2" title={property.title || property.url}>
            {property.title || (property.url || '').replace(/^https?:\/\/(www\.)?/, '')}
          </h3>
          {stats.length > 0 && (
            <p className="mt-1 text-[13px] text-secondary tabular truncate">{stats.join(' · ')}</p>
          )}
        </button>

        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted min-w-0">
            {property.forSale && <span className="px-1.5 py-0.5 rounded bg-page border border-line text-secondary">Sale</span>}
            {property.forRent && <span className="px-1.5 py-0.5 rounded bg-page border border-line text-secondary">Rent</span>}
            <span className="truncate">
              {property.lastReportedUpdateDate ? `Updated ${property.lastReportedUpdateDate}` : ''}
            </span>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <Menu items={menuItems} label={`Actions for ${property.title || 'property'}`} />
          </div>
        </div>
      </div>

      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => onDelete(property.id)}
        property={property}
      />
    </article>
  );
};

export default PropertyCard;
